// ═══════════════════════════════════════
// IMPORT — Modale d'importation de fichiers (ABC, BWW)
// Bundlé dans common.min.js via common/main.js
// ═══════════════════════════════════════

import { sessionToken } from './state.js';
import { openUserModal, authHeaders } from './common/auth.js';
import { openModal, closeModal }      from './modal.js';
import { t, lang }                    from './i18n.js';

const API_IMPORT = '/api/import.php';

let _currentAbc        = null;
let _currentFormat     = 'abc';
let _currentInstrument = null;

// ── Point d'entrée public ─────────────────────────────────────────────────────
export function openImportModal() {
    if (!sessionToken) {
        openUserModal('user-login');
        return;
    }
    openModal('import-partition', _mountImportView);
}

// ── Initialisation de la vue ──────────────────────────────────────────────────
async function _mountImportView(nav, controller) {
    const { signal } = controller;

    _currentAbc    = null;
    _currentFormat = 'abc';

    const tabs       = document.getElementById('import-format-tabs');
    const dropzone   = document.getElementById('import-dropzone');
    const fileInput  = document.getElementById('import-file-input');
    const extLabel   = document.getElementById('import-dropzone-ext');
    const dropLabel  = document.getElementById('import-dropzone-label');
    const preview    = document.getElementById('import-preview');
    const previewAbc = document.getElementById('import-preview-abc');
    const previewTtl = document.getElementById('import-preview-title');
    const resetBtn   = document.getElementById('import-reset');
    const confirmBtn = document.getElementById('import-confirm');
    const errorEl    = document.getElementById('import-error');
    const cancelBtn  = document.querySelector('#modal-content .btn-cancel');

    if (cancelBtn) cancelBtn.addEventListener('click', () => closeModal(), { signal });

    // ── Onglets format ──
    tabs?.addEventListener('click', (e) => {
        const tab = e.target.closest('.import-tab');
        if (!tab) return;
        _currentFormat = tab.dataset.format;
        tabs.querySelectorAll('.import-tab').forEach(t => t.classList.remove('active'));
        tab.classList.add('active');
        if (extLabel)  extLabel.textContent = '.' + _currentFormat;
        if (fileInput) fileInput.accept     = '.' + _currentFormat;
        _resetPreview();
    }, { signal });

    // ── Click dropzone ──
    dropzone?.addEventListener('click', () => {
        if (!preview || preview.style.display === 'none') fileInput?.click();
    }, { signal });

    // ── Drag & drop ──
    dropzone?.addEventListener('dragover', (e) => {
        e.preventDefault();
        dropzone.classList.add('drag-over');
    }, { signal });
    dropzone?.addEventListener('dragleave', () => {
        dropzone.classList.remove('drag-over');
    }, { signal });
    dropzone?.addEventListener('drop', (e) => {
        e.preventDefault();
        dropzone.classList.remove('drag-over');
        const file = e.dataTransfer?.files?.[0];
        if (file) _handleFile(file);
    }, { signal });

    // ── Sélection fichier ──
    fileInput?.addEventListener('change', () => {
        const file = fileInput.files?.[0];
        if (file) _handleFile(file);
    }, { signal });

    // ── Reset preview ──
    resetBtn?.addEventListener('click', _resetPreview, { signal });

    // ── Confirmer ──
    confirmBtn?.addEventListener('click', _doImport, { signal });

    // ── Helpers ──────────────────────────────────────────────────────────────

    function _resetPreview() {
        _currentAbc = null;
        if (preview)    preview.style.display  = 'none';
        if (dropzone)   dropzone.style.display = '';
        if (confirmBtn) confirmBtn.disabled    = true;
        if (errorEl)    errorEl.textContent    = '';
        if (fileInput)  fileInput.value        = '';
    }

    function _showError(msg) {
        if (errorEl) errorEl.textContent = msg;
    }

    function _handleFile(file) {
        const ext = file.name.split('.').pop().toLowerCase();
        if (ext !== _currentFormat) {
            _showError(t('import.error.wrong_format', { ext: _currentFormat.toUpperCase() }));
            return;
        }
        _showError('');
        _uploadAndPreview(file);
    }

    async function _uploadAndPreview(file) {
        if (dropLabel)  dropLabel.textContent = t('import.converting');
        if (confirmBtn) confirmBtn.disabled   = true;

        const fd = new FormData();
        fd.append('file', file);

        // Ne pas forcer Content-Type : le navigateur pose le boundary multipart
        const headers = sessionToken ? { 'Authorization': 'Bearer ' + sessionToken } : {};

        try {
            const res  = await fetch(API_IMPORT, { method: 'POST', headers, body: fd });
            const data = await res.json();

            if (!data.success) {
                _showError(data.error || t('import.error.generic'));
                if (dropLabel) dropLabel.textContent = t('modal.import.drop_hint');
                return;
            }

            _currentAbc = data.abc;
            _currentInstrument = data.instrument ?? null;

            const mTitle = data.abc.match(/^T:\s*(.+)$/m);
            const title  = mTitle ? mTitle[1].trim() : file.name;

            if (previewTtl) previewTtl.textContent = title;
            if (previewAbc) previewAbc.textContent = data.abc.length > 800
                ? data.abc.slice(0, 800) + '\n…'
                : data.abc;
            if (preview)    preview.style.display  = '';
            if (dropzone)   dropzone.style.display = 'none';
            if (confirmBtn) confirmBtn.disabled    = false;

        } catch (e) {
            _showError(t('import.error.network'));
            if (dropLabel) dropLabel.textContent = t('modal.import.drop_hint');
        }
    }

    async function _doImport() {
        if (!_currentAbc) return;

        try {
            const res  = await fetch('/api/partitions.php?action=quota', { headers: authHeaders() });
            const data = await res.json();
            if (!data.can_create) {
                _showError(t('nav.quota_reached'));
                return;
            }
        } catch (e) {
            _showError(t('import.error.network'));
            return;
        }

        sessionStorage.setItem('scorou_import_abc', _currentAbc);
        if (_currentInstrument) {
            sessionStorage.setItem('scorou_import_instrument', _currentInstrument);
        } else {
            sessionStorage.removeItem('scorou_import_instrument');
        }
        closeModal();

        const target = lang() === 'en' ? '/en/partitions/new?import=1' : '/partitions/new?import=1';
        window.location.href = target;
    }
}

// ── Initialisation du bouton sur les pages statiques ─────────────────────────
// Appelé depuis common/main.js dans le DOMContentLoaded.
// S'attache à tout bouton #btn-import-partition présent dans le DOM.
export function initImportButton() {
    const btn = document.getElementById('btn-import-partition');
    if (!btn) return;
    btn.addEventListener('click', openImportModal);
}